/**
 * Analytics: conversation volume over time, plus stat tiles with deltas.
 *
 * Chart decisions, following the project's data-visualisation rules:
 *
 * - One y-axis. All three series are counts of conversations, so they share a
 *   scale honestly; a second axis would invent a relationship between them.
 * - Categorical hues assigned in fixed order and never cycled, so a series
 *   keeps its colour when another is toggled off. The three slots used here
 *   are validated for colour-vision deficiency separation against the light
 *   surface.
 * - A legend is always present, so identity never depends on colour alone,
 *   and a table view is available — which is also the required relief for the
 *   one series whose hue sits below 3:1 contrast on a light surface.
 * - Days with no conversations are plotted as zero rather than skipped, so a
 *   quiet week reads as quiet instead of as a straight line.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { api } from '../api/client';
import type { AnalyticsResponse, StatDelta } from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Spinner,
} from '../components/ui';
import { useBots } from '../hooks/useBots';

/** Categorical slots 1–3, in fixed order. Never cycled, never reassigned. */
const SERIES = [
  { key: 'conversations', label: 'Total conversations', color: '#2a78d6' },
  { key: 'ai_resolved', label: 'AI resolved', color: '#eb6834' },
  { key: 'human_involved', label: 'Human involved', color: '#1baf7a' },
] as const;

const GRID = '#e6e9ed';
const AXIS_TEXT = '#6b7785';

export const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
] as const;

export function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - (days - 1));
  return date.toISOString().slice(0, 10);
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function Analytics(): JSX.Element {
  const { selected } = useBots();
  const slug = selected?.slug ?? null;

  const [start, setStart] = useState(() => isoDaysAgo(30));
  const [end, setEnd] = useState(today);
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTable, setShowTable] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      setData(await api.analytics(slug, start, end));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load analytics');
    } finally {
      setLoading(false);
    }
  }, [slug, start, end]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!slug) return <EmptyState title="No bot selected" />;

  const inputClass =
    'rounded-md border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-brand-blue';

  return (
    <div>
      <PageHeader
        title="Analytics"
        description={
          data ? `${data.start} to ${data.end}` : 'Conversation volume and outcomes'
        }
        actions={
          <Button
            variant="secondary"
            onClick={() => void api.downloadAnalyticsCsv(slug, start, end)}
          >
            Export CSV
          </Button>
        }
      />

      {error && <ErrorNotice message={error} />}

      {/* Filters in a single row above the charts. */}
      <Card className="mb-4 flex flex-wrap items-center gap-2 p-3">
        {RANGES.map((range) => (
          <button
            key={range.days}
            type="button"
            onClick={() => {
              setStart(isoDaysAgo(range.days));
              setEnd(today());
            }}
            className={`rounded-md px-2.5 py-1 text-xs font-medium ${
              start === isoDaysAgo(range.days) && end === today()
                ? 'bg-brand-navy text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {range.label}
          </button>
        ))}
        <span className="ml-2 text-sm text-slate-500">from</span>
        <input
          type="date"
          value={start}
          max={end}
          onChange={(event) => setStart(event.target.value)}
          className={inputClass}
          aria-label="Start date"
        />
        <span className="text-sm text-slate-500">to</span>
        <input
          type="date"
          value={end}
          min={start}
          onChange={(event) => setEnd(event.target.value)}
          className={inputClass}
          aria-label="End date"
        />
        <button
          type="button"
          onClick={() => setShowTable((current) => !current)}
          className="ml-auto rounded-md px-2.5 py-1 text-xs font-medium text-slate-600 underline hover:text-slate-900"
        >
          {showTable ? 'Show chart' : 'Show table'}
        </button>
      </Card>

      {loading || !data ? (
        <Spinner label="Loading analytics…" />
      ) : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="AI chatbot conversations" stat={data.conversations} />
            <StatTile label="AI-resolved conversations" stat={data.ai_resolved} />
            <StatTile label="Positive feedback" stat={data.positive_feedback} />
            <StatTile
              label="Negative feedback"
              stat={data.negative_feedback}
              lowerIsBetter
            />
          </div>

          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Contacts captured" stat={data.contacts_captured} />
            <StatTile
              label="Leads recommended to NetSuite"
              stat={data.leads_recommended}
            />
            <StatTile label="Human-involved conversations" stat={data.human_involved} />
          </div>

          <Card className="p-4">
            <h2 className="mb-1 text-base font-semibold text-slate-900">
              Conversations per day
            </h2>
            <p className="mb-3 text-xs text-slate-500">
              Compared with {data.previous_start} to {data.previous_end}.
            </p>

            {showTable ? (
              <DailyTable data={data} />
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={data.daily}
                    margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
                  >
                    <defs>
                      {SERIES.map((series) => (
                        <linearGradient
                          key={series.key}
                          id={`fill-${series.key}`}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          {/* A wash, never a saturated block. */}
                          <stop
                            offset="0%"
                            stopColor={series.color}
                            stopOpacity={0.12}
                          />
                          <stop
                            offset="100%"
                            stopColor={series.color}
                            stopOpacity={0.02}
                          />
                        </linearGradient>
                      ))}
                    </defs>

                    {/* Recessive, hairline, solid — never dashed. */}
                    <CartesianGrid stroke={GRID} strokeWidth={1} vertical={false} />
                    <XAxis
                      dataKey="day"
                      tick={{ fill: AXIS_TEXT, fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: GRID }}
                      minTickGap={24}
                      tickFormatter={(value: string) => value.slice(5)}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: AXIS_TEXT, fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={36}
                    />
                    <Tooltip
                      cursor={{ stroke: AXIS_TEXT, strokeWidth: 1 }}
                      contentStyle={{
                        borderRadius: 8,
                        border: `1px solid ${GRID}`,
                        fontSize: 12,
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 12, color: AXIS_TEXT }}
                      iconType="plainline"
                    />

                    {SERIES.map((series) => (
                      <Area
                        key={series.key}
                        type="monotone"
                        dataKey={series.key}
                        name={series.label}
                        stroke={series.color}
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill={`url(#fill-${series.key})`}
                        // ≥ 8px marker with a 2px surface ring, so points stay
                        // legible where the series cross.
                        activeDot={{ r: 4, strokeWidth: 2, stroke: '#ffffff' }}
                        dot={false}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export function StatTile({
  label,
  stat,
  lowerIsBetter = false,
}: {
  label: string;
  stat: StatDelta;
  lowerIsBetter?: boolean;
}): JSX.Element {
  const improved = lowerIsBetter ? stat.change < 0 : stat.change > 0;
  const unchanged = stat.change === 0;

  return (
    <Card className="p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
        {stat.value.toLocaleString()}
      </p>
      <p
        className={`mt-0.5 text-xs ${
          unchanged ? 'text-slate-400' : improved ? 'text-emerald-700' : 'text-red-700'
        }`}
      >
        {unchanged
          ? 'No change'
          : `${stat.change > 0 ? '+' : ''}${stat.change.toLocaleString()}`}
        {stat.percent !== null && !unchanged && ` (${stat.percent}%)`}
        <span className="text-slate-400"> vs previous period</span>
      </p>
    </Card>
  );
}

function DailyTable({ data }: { data: AnalyticsResponse }): JSX.Element {
  return (
    <div className="max-h-80 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">Date</th>
            {SERIES.map((series) => (
              <th key={series.key} className="px-3 py-2 text-right font-medium">
                {series.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {data.daily.map((point) => (
            <tr key={point.day}>
              <td className="px-3 py-1.5 text-slate-700">{point.day}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                {point.conversations}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                {point.ai_resolved}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                {point.human_involved}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
